import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";

export function AboutPage() {
  const revealRefs = useRef<HTMLElement[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("opacity-100", "translate-y-0");
            entry.target.classList.remove("opacity-0", "translate-y-8");
          }
        });
      },
      { threshold: 0.15 },
    );
    revealRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const addRevealRef = (el: HTMLElement | null) => {
    if (el && !revealRefs.current.includes(el)) {
      revealRefs.current.push(el);
    }
  };

  return (
    <>
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 md:px-10 py-5 bg-void/70 backdrop-blur-xl border-b border-border/50">
        <Link
          to="/"
          className="font-display text-2xl spectral-gradient-text"
        >
          Espectro
        </Link>
        <div className="flex items-center gap-6 md:gap-8">
          <Link
            to="/"
            className="hidden md:inline font-heading text-sm font-medium text-mist hover:text-ghost-white transition-colors"
          >
            Home
          </Link>
          <Link
            to="/app"
            className="spectral-gradient-bg px-5 py-2 rounded-lg text-white font-heading text-sm font-semibold hover:opacity-90 hover:shadow-[0_0_30px_rgba(124,58,237,0.3)] transition-all"
          >
            Launch App
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-40 pb-20 px-6 text-center overflow-hidden">
        <div className="absolute w-[500px] h-[500px] rounded-full bg-[radial-gradient(circle,rgba(124,58,237,0.12)_0%,rgba(99,102,241,0.06)_40%,transparent_100%)] blur-[80px] pointer-events-none top-[10%] left-1/2 -translate-x-1/2" />

        <div className="relative z-10 max-w-2xl mx-auto">
          <div className="font-heading text-xs font-semibold uppercase tracking-[0.15em] text-spectral-violet mb-4">
            How Espectro works
          </div>
          <h1 className="font-display text-[clamp(2.5rem,6vw,4.5rem)] font-normal leading-[1] mb-6 spectral-gradient-text">
            Enter visible. Exit invisible.
          </h1>
          <p className="text-[1.1rem] text-mist leading-relaxed max-w-xl mx-auto">
            Your funds are swapped into ZEC and enter the Zcash Orchard shielded
            pool &mdash; a cryptographically private ledger where balances are
            invisible and unlinkable. After passing through the pool, they are
            routed to your destination wallet as the asset you chose. One click,
            one wallet signature, twenty minutes.
          </p>
        </div>
      </section>

      {/* Architecture */}
      <section
        ref={addRevealRef}
        className="max-w-[1100px] mx-auto px-6 py-24 opacity-0 translate-y-8 transition-all duration-700"
      >
        <div className="font-heading text-xs font-semibold uppercase tracking-[0.15em] text-spectral-violet mb-3">
          Architecture
        </div>
        <h2 className="font-heading text-[clamp(2rem,4vw,2.8rem)] font-semibold text-ghost-white mb-4 tracking-tight">
          Four primitives, one pipeline
        </h2>
        <p className="text-[1.05rem] text-mist max-w-xl leading-relaxed mb-16">
          Espectro composes production-grade cryptographic systems into an
          automated transfer pipeline. No custom privacy contracts, no novel
          cryptography.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {ARCHITECTURE.map((block) => (
            <div
              key={block.title}
              className="p-8 rounded-2xl bg-surface border border-border hover:border-spectral-violet/25 transition-colors"
            >
              <div className="font-heading text-lg font-semibold text-ghost-white mb-3">
                {block.title}
              </div>
              <p className="text-sm text-mist leading-relaxed">
                {block.body}
              </p>
              {block.detail && (
                <p className="text-xs text-ash leading-relaxed mt-3">
                  {block.detail}
                </p>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Trust Model */}
      <section
        ref={addRevealRef}
        className="max-w-[1100px] mx-auto px-6 py-24 opacity-0 translate-y-8 transition-all duration-700"
      >
        <div className="font-heading text-xs font-semibold uppercase tracking-[0.15em] text-spectral-violet mb-3">
          Trust model
        </div>
        <h2 className="font-heading text-[clamp(2rem,4vw,2.8rem)] font-semibold text-ghost-white mb-4 tracking-tight">
          Four pillars
        </h2>
        <p className="text-[1.05rem] text-mist max-w-xl leading-relaxed mb-16">
          Espectro is designed so that you never have to trust Espectro.
        </p>

        <div className="space-y-5">
          {TRUST_PILLARS.map((pillar) => (
            <div
              key={pillar.title}
              className="p-8 rounded-2xl bg-surface border border-border"
            >
              <div className="font-heading text-lg font-semibold text-ghost-white mb-2">
                {pillar.title}
              </div>
              <p className="text-sm text-mist leading-relaxed">
                {pillar.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* What Espectro Is Not */}
      <section
        ref={addRevealRef}
        className="max-w-[1100px] mx-auto px-6 py-24 opacity-0 translate-y-8 transition-all duration-700"
      >
        <div className="font-heading text-xs font-semibold uppercase tracking-[0.15em] text-spectral-violet mb-3">
          Clarifications
        </div>
        <h2 className="font-heading text-[clamp(2rem,4vw,2.8rem)] font-semibold text-ghost-white mb-16 tracking-tight">
          What Espectro is not
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {NOT_LIST.map((item) => (
            <div
              key={item.title}
              className="p-8 rounded-2xl bg-surface border border-border"
            >
              <div className="font-heading text-base font-semibold text-ghost-white mb-2">
                {item.title}
              </div>
              <p className="text-sm text-mist leading-relaxed">{item.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Cost */}
      <section
        ref={addRevealRef}
        className="max-w-[1100px] mx-auto px-6 py-24 opacity-0 translate-y-8 transition-all duration-700"
      >
        <div className="font-heading text-xs font-semibold uppercase tracking-[0.15em] text-spectral-violet mb-3">
          Cost
        </div>
        <h2 className="font-heading text-[clamp(2rem,4vw,2.8rem)] font-semibold text-ghost-white mb-4 tracking-tight">
          What a transfer costs
        </h2>
        <p className="text-[1.05rem] text-mist max-w-xl leading-relaxed mb-16">
          Typical total cost is 2&ndash;4% of the transfer amount. No Espectro
          protocol fee is charged in v1.
        </p>

        <div className="overflow-x-auto rounded-2xl border border-border">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-border bg-surface">
                <th className="px-6 py-4 font-heading text-xs font-semibold text-ghost-white uppercase tracking-wider">
                  Component
                </th>
                <th className="px-6 py-4 font-heading text-xs font-semibold text-ghost-white uppercase tracking-wider">
                  Typical
                </th>
                <th className="px-6 py-4 font-heading text-xs font-semibold text-ghost-white uppercase tracking-wider">
                  Worst case
                </th>
              </tr>
            </thead>
            <tbody className="text-sm text-mist font-body">
              {COST_ROWS.map((row, i) => (
                <tr
                  key={row.component}
                  className={
                    i < COST_ROWS.length - 1 ? "border-b border-border/60" : ""
                  }
                >
                  <td className="px-6 py-3.5 font-heading text-ghost-white text-sm">
                    {row.component}
                  </td>
                  <td className="px-6 py-3.5">{row.typical}</td>
                  <td className="px-6 py-3.5">{row.worst}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-xs text-ash mt-4 leading-relaxed max-w-xl">
          Swap slippage and route fees dominate the cost. Zcash network fees and
          source-chain gas are negligible for any transfer above a few dollars.
          Fixed route fees have a proportionally larger impact on smaller
          amounts.
        </p>
      </section>

      {/* Competitive Positioning */}
      <section
        ref={addRevealRef}
        className="max-w-[1100px] mx-auto px-6 py-24 opacity-0 translate-y-8 transition-all duration-700"
      >
        <div className="font-heading text-xs font-semibold uppercase tracking-[0.15em] text-spectral-violet mb-3">
          Comparison
        </div>
        <h2 className="font-heading text-[clamp(2rem,4vw,2.8rem)] font-semibold text-ghost-white mb-16 tracking-tight">
          How Espectro compares
        </h2>

        <div className="overflow-x-auto rounded-2xl border border-border">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-border bg-surface">
                <th className="px-6 py-4 font-heading text-xs font-semibold text-ghost-white uppercase tracking-wider">
                  Approach
                </th>
                <th className="px-6 py-4 font-heading text-xs font-semibold text-ghost-white uppercase tracking-wider">
                  Anonymity set
                </th>
                <th className="px-6 py-4 font-heading text-xs font-semibold text-ghost-white uppercase tracking-wider">
                  Custody
                </th>
                <th className="px-6 py-4 font-heading text-xs font-semibold text-ghost-white uppercase tracking-wider">
                  Automation
                </th>
              </tr>
            </thead>
            <tbody className="text-sm text-mist font-body">
              {COMPARISONS.map((row, i) => (
                <tr
                  key={row.approach}
                  className={`${i < COMPARISONS.length - 1 ? "border-b border-border/60" : ""} ${row.highlight ? "bg-spectral-violet/[0.04]" : ""}`}
                >
                  <td className="px-6 py-3.5 font-heading text-ghost-white text-sm">
                    {row.approach}
                  </td>
                  <td className="px-6 py-3.5">{row.anonymitySet}</td>
                  <td className="px-6 py-3.5">{row.custody}</td>
                  <td className="px-6 py-3.5">{row.automation}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* CTA */}
      <section className="relative px-6 py-32 text-center overflow-hidden">
        <div className="absolute w-[500px] h-[500px] rounded-full bg-[radial-gradient(circle,rgba(124,58,237,0.1)_0%,transparent_70%)] blur-[80px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
        <h2
          ref={addRevealRef}
          className="font-display text-[clamp(2.5rem,5vw,4rem)] font-normal text-ghost-white mb-5 relative z-10 opacity-0 translate-y-8 transition-all duration-700"
        >
          Ready to disappear?
        </h2>
        <p
          ref={addRevealRef}
          className="text-[1.05rem] text-mist mb-10 relative z-10 opacity-0 translate-y-8 transition-all duration-700"
        >
          Break the link between your wallets. Start using Espectro today.
        </p>
        <Link
          to="/app"
          className="relative z-10 inline-flex items-center gap-2.5 px-9 py-3.5 rounded-lg spectral-gradient-bg text-white font-heading text-lg font-semibold hover:shadow-[0_0_50px_rgba(124,58,237,0.35),0_0_100px_rgba(99,102,241,0.15)] hover:-translate-y-0.5 transition-all"
        >
          Launch App <span className="text-xl">&#8594;</span>
        </Link>
      </section>

      {/* Footer */}
      <footer className="max-w-[1100px] mx-auto px-6 md:px-10 py-12 border-t border-border flex flex-col items-center gap-3">
        <span className="font-display text-xl text-ash">Espectro</span>
        <div className="flex items-center gap-5">
          <Link
            to="/"
            className="text-xs text-ash/50 hover:text-ash transition-colors font-body"
          >
            Home
          </Link>
          <a
            href="mailto:espectrosupport@proton.me"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-ash/50 hover:text-ash transition-colors font-body"
          >
            Support
          </a>
        </div>
      </footer>
    </>
  );
}

const ARCHITECTURE = [
  {
    title: "Zcash Orchard pool",
    body: "The privacy layer. Funds are shielded using Halo 2 zero-knowledge proofs on the Zcash L1, benefiting from the full network anonymity set. Not a custom contract or app-chain.",
    detail:
      "Halo 2 is a recursive, trustless proof system \u2014 no trusted setup ceremony. The Orchard pool is the same pool used by every shielded Zcash transaction, so your transfer blends into the entire network\u2019s activity.",
  },
  {
    title: "Lit Protocol MPC",
    body: "Ephemeral Programmable Key Pairs (PKPs) are created per transfer. Threshold cryptography distributes key shares across the Lit node network. No single node can sign unilaterally.",
    detail:
      "The PKP is destroyed after completion, providing forward secrecy. Even if the Lit network were compromised after your transfer, the key no longer exists.",
  },
  {
    title: "TEE-based proving",
    body: "Halo 2 proof generation runs natively \u2014 not in WASM \u2014 inside a Trusted Execution Environment on Phala Network. The prover never sees keys or seeds.",
    detail:
      "Stateless, no logging, TLS-only communication. Only serialized circuit data enters the TEE. The prover has no ability to reconstruct wallet information.",
  },
  {
    title: "Zero persistence",
    body: "Static frontend, stateless proving service inside a TEE, localStorage wiped on completion. No database, no user accounts, no data at rest when idle.",
    detail:
      "There is no backend server with signing authority. The only server-side component is the stateless prover, which retains nothing between requests.",
  },
];

const TRUST_PILLARS = [
  {
    title: "Full custody, every second",
    body: "Your funds are never held by Espectro, by a smart contract, or by any third party. They move through an ephemeral MPC wallet that only you can authorize \u2014 created for your transfer, destroyed after. Lit Protocol\u2019s threshold cryptography means no single node ever holds a complete key.",
  },
  {
    title: "Real cryptography, not a contract",
    body: "Espectro doesn\u2019t use a mixing smart contract. It uses the Zcash Orchard shielded pool \u2014 the same pool used by the entire Zcash network. Your funds benefit from the anonymity set of every shielded transaction on Zcash, not just other Espectro users.",
  },
  {
    title: "Nothing stored, nothing to subpoena",
    body: "There is no database, no user accounts, no stored data. The only backend is a stateless proving server running inside a TEE \u2014 it holds no keys, logs nothing, and retains nothing between requests. When your transfer completes, the app erases all local state. There is literally nothing left.",
  },
  {
    title: "Recoverable at every step",
    body: "If anything fails mid-transfer, your funds are never lost. The ephemeral wallet persists until you\u2019ve safely recovered. You can resume, retry, or manually extract at any point.",
  },
];

const NOT_LIST = [
  {
    title: "Not a mixer",
    body: "There is no shared pool contract, no reliance on other Espectro users for anonymity. Your funds benefit from the entire Zcash network\u2019s anonymity set.",
  },
  {
    title: "Not custodial",
    body: "There is no operator holding your funds. The ephemeral MPC wallet is authorized solely by you and destroyed after the transfer.",
  },
  {
    title: "Not a service",
    body: "There are no accounts, no database, no stored data. The only server is a stateless prover inside a TEE \u2014 it retains nothing between requests. When you\u2019re done, nothing remains.",
  },
  {
    title: "Not a protocol token",
    body: "There is no token, no governance, no staking. Espectro is a tool.",
  },
];

const COST_ROWS = [
  {
    component: "Combined swap slippage",
    typical: "< 2%",
    worst: "~5.9%",
  },
  {
    component: "Route fees (enter + exit)",
    typical: "$1\u2013$6",
    worst: "Varies with amount",
  },
  {
    component: "Zcash network fees",
    typical: "< $0.01",
    worst: "< $0.01",
  },
  {
    component: "Source chain gas",
    typical: "< $0.01",
    worst: "< $0.10",
  },
  {
    component: "Espectro protocol fee",
    typical: "None (v1)",
    worst: "None (v1)",
  },
];

const COMPARISONS = [
  {
    approach: "Tornado Cash\u2013style mixers",
    anonymitySet: "Other mixer users only",
    custody: "Smart contract (sanctioned)",
    automation: "Manual",
    highlight: false,
  },
  {
    approach: "Manual Zcash usage",
    anonymitySet: "Full Zcash network",
    custody: "Self-custodial",
    automation: "None",
    highlight: false,
  },
  {
    approach: "CEX deposit / withdraw",
    anonymitySet: "None (full KYC link)",
    custody: "Custodial",
    automation: "N/A",
    highlight: false,
  },
  {
    approach: "Railgun / privacy DeFi",
    anonymitySet: "Other protocol users",
    custody: "Smart contract",
    automation: "Partial",
    highlight: false,
  },
  {
    approach: "Espectro",
    anonymitySet: "Full Zcash network",
    custody: "Non-custodial (ephemeral MPC)",
    automation: "Fully automatic",
    highlight: true,
  },
];
